// src/data/regions/fishIQSeaSpeciesBuffV2.ts
// Roddo FishIQ Southeast Asia catalog buff v2.
// Adds more useful Thailand / Philippines / Indonesia local species without making a generic Asia pack.
// Static/editorial guidance only. Not regulatory advice, official science, or exact-location claims.

import type { FishIQSpeciesSeed } from '../../types';

type FishIQTier = 'common_target' | 'local_common' | 'trophy' | 'education_sensitive';
type FishIQSeaPackId = 'thailand' | 'philippines' | 'indonesia';
type RoddoSeaFishIQSeed = FishIQSpeciesSeed & { fishIQTier?: FishIQTier; regionPackIds?: FishIQSeaPackId[] };

const source = { title: 'Roddo Southeast Asia species expansion v2', sourceType: 'editorial' as const };

const species = (input: RoddoSeaFishIQSeed): RoddoSeaFishIQSeed => ({
  confidence: 'editorial',
  editorialSources: [source],
  ...input,
});

const SEA_SHARED_FISHIQ_SPECIES_BUFF_V2_SEED: RoddoSeaFishIQSeed[] = [
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "cobia_sea",
    slug: "cobia-sea",
    commonName: "Cobia",
    scientificName: "Rachycentron canadum",
    aliases: ["Ling", "Black Kingfish"],
    family: "cobia",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["inshore", "reef", "ocean", "bay"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A strong cruising predator often associated with buoys, reefs, current lines, rays, and bait concentrations.",
    feedingProfile: { primaryPrey: ["baitfish", "crabs", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Live Bait", "Swimbait", "Jig", "Crab"],
    bestTechniques: ["Cast to visible cruisers", "Check buoys and current lines", "Use stout tackle"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["roaming predator", "structure-associated"],
    structureTags: ["buoys", "reefs", "current lines", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["roaming predator", "structure-associated"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "wahoo_sea",
    slug: "wahoo-sea",
    commonName: "Wahoo",
    scientificName: "Acanthocybium solandri",
    aliases: [],
    family: "mackerel",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["ocean", "reef", "drop-off"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A high-speed pelagic predator usually tied to bluewater edges, bait, reefs, and current breaks.",
    feedingProfile: { primaryPrey: ["flying fish", "small tuna", "baitfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Trolling Plug", "High-speed Lure", "Jig", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["pelagic predator", "bait-tracker"],
    structureTags: ["drop-offs", "reef edges", "current lines"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["pelagic predator", "bait-tracker"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "longtail_tuna",
    slug: "longtail-tuna",
    commonName: "Longtail Tuna",
    scientificName: "Thunnus tonggol",
    aliases: ["Northern Bluefin Tuna (regional common usage varies)"],
    family: "tuna",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["inshore", "ocean", "bay"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A coastal tuna often found nearer shore than larger oceanic tuna, tracking bait schools and current.",
    feedingProfile: { primaryPrey: ["small baitfish", "squid", "anchovies"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Metal Jig", "Trolling Feather", "Live Bait", "Small Plug"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "bait-tracker"],
    structureTags: ["bait schools", "current seams", "nearshore drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bait-tracker"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "bluefin_trevally",
    slug: "bluefin-trevally",
    commonName: "Bluefin Trevally",
    scientificName: "Caranx melampygus",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "inshore", "ocean"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A reef and lagoon predator that often patrols clear-water edges, current, bait, and coral structure.",
    feedingProfile: { primaryPrey: ["baitfish", "crustaceans", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Popping Plug", "Stickbait", "Metal Jig", "Soft Plastic"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef predator", "roaming predator"],
    structureTags: ["reef edges", "lagoon passes", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef predator", "roaming predator"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "golden_trevally",
    slug: "golden-trevally",
    commonName: "Golden Trevally",
    scientificName: "Gnathanodon speciosus",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "sand flat", "inshore"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A striking trevally often found around reefs, sand flats, rays, and current-fed edges.",
    feedingProfile: { primaryPrey: ["crustaceans", "small fish", "worms"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Jig", "Shrimp Imitation", "Soft Plastic", "Fly"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["roaming predator", "flat/reef edge"],
    structureTags: ["sand flats", "reef edges", "rays", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["roaming predator", "flat/reef edge"],
  }),
  species({
    regionPackIds: ["indonesia", "philippines", "thailand"],
    id: "coral_trout",
    slug: "coral-trout",
    commonName: "Coral Trout",
    scientificName: "Plectropomus leopardus",
    aliases: ["Leopard Coral Grouper"],
    family: "grouper",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "lagoon", "ocean"],
    regions: ["Indonesia", "Philippines", "Thailand", "Indo-Pacific"],
    summary: "A reef predator associated with coral edges, bommies, drop-offs, and ambush lanes.",
    feedingProfile: { primaryPrey: ["small reef fish", "squid", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Jig", "Live Bait", "Soft Plastic", "Minnow Plug"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "reef fish"],
    structureTags: ["coral edges", "bommies", "drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "reef fish"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "emperor_fish",
    slug: "emperor-fish",
    commonName: "Emperor Fish",
    scientificName: "Lethrinus spp.",
    aliases: ["Lethrinid", "Emperor Snapper"],
    family: "emperor",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "sand flat", "lagoon"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A reef/sand-edge group often associated with rubble, channels, lagoons, and bottom forage.",
    feedingProfile: { primaryPrey: ["crabs", "shrimp", "small fish", "mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Squid", "Jig", "Cut Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "reef edge"],
    structureTags: ["rubble", "sand edges", "reef channels"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "reef edge"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "jobfish",
    slug: "jobfish",
    commonName: "Jobfish",
    scientificName: "Aprion / Pristipomoides spp.",
    aliases: ["Green Jobfish", "Deepwater Jobfish"],
    family: "snapper",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "drop-off", "ocean"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A deep reef/drop-off snapper group often linked to current, ledges, and bait over structure.",
    feedingProfile: { primaryPrey: ["baitfish", "squid", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Metal Jig", "Cut Bait", "Live Bait", "Slow Pitch Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef predator", "current-aware feeder"],
    structureTags: ["ledges", "drop-offs", "deep reefs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef predator", "current-aware feeder"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "threadfin_bream",
    slug: "threadfin-bream",
    commonName: "Threadfin Bream",
    scientificName: "Nemipterus spp.",
    aliases: [],
    family: "threadfin bream",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["sand", "reef", "inshore"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A common demersal group around sandy reef edges, rubble, and coastal bottom structure.",
    feedingProfile: { primaryPrey: ["worms", "shrimp", "small crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Squid", "Small Jig", "Bottom Rig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "local_common"],
    structureTags: ["sand edges", "rubble", "coastal bottom"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "local_common"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "rabbitfish_sea",
    slug: "rabbitfish-sea",
    commonName: "Rabbitfish",
    scientificName: "Siganus spp.",
    aliases: ["Kitang", "Baronang", "Samaral"],
    family: "rabbitfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "seagrass", "mangrove", "inshore"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A common reef/seagrass fish with strong local food-fish identity across Southeast Asia.",
    feedingProfile: { primaryPrey: ["algae", "seagrass", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Algae Bait", "Bread", "Small Shrimp", "Tiny Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["grazing fish", "reef/seagrass"],
    structureTags: ["seagrass", "mangroves", "reef flats"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["grazing fish", "reef/seagrass"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "tarpon_indo_pacific",
    slug: "tarpon-indo-pacific",
    commonName: "Oxeye Tarpon",
    scientificName: "Megalops cyprinoides",
    aliases: ["Indo-Pacific Tarpon"],
    family: "tarpon",
    category: "both",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater", "brackish", "saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["estuary", "river", "mangrove", "bay"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A smaller tarpon relative often associated with estuaries, mangroves, brackish creeks, and rolling surface activity.",
    feedingProfile: { primaryPrey: ["small fish", "shrimp", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Swimbait", "Shrimp Imitation", "Fly", "Minnow Plug"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["rolling fish", "estuary predator"],
    structureTags: ["mangroves", "creek mouths", "brackish drains"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["rolling fish", "estuary predator"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "needlefish_sea",
    slug: "needlefish-sea",
    commonName: "Needlefish",
    scientificName: "Belonidae spp.",
    aliases: [],
    family: "needlefish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["inshore", "reef", "bay", "surface"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A surface-oriented predator often seen around lights, bait schools, piers, and reef flats.",
    feedingProfile: { primaryPrey: ["small baitfish", "surface bait"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Plug", "Fly", "Small Spoon", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["surface predator", "light-line fish"],
    structureTags: ["lights", "reef flats", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["surface predator", "light-line fish"],
  }),
  species({
    regionPackIds: ["thailand", "philippines", "indonesia"],
    id: "barracuda_sea",
    slug: "barracuda-sea",
    commonName: "Barracuda",
    scientificName: "Sphyraena spp.",
    aliases: [],
    family: "barracuda",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "inshore", "ocean", "bay"],
    regions: ["Thailand", "Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A toothy ambush/roaming predator often tied to reefs, bait schools, current lines, and clear-water edges.",
    feedingProfile: { primaryPrey: ["baitfish", "small reef fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Jerkbait", "Spoon", "Stickbait", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "roaming predator"],
    structureTags: ["reef edges", "bait schools", "drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "roaming predator"],
  }),
];

const THAILAND_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED: RoddoSeaFishIQSeed[] = [
  species({
    regionPackIds: ["thailand"],
    id: "archerfish",
    slug: "archerfish",
    commonName: "Archerfish",
    scientificName: "Toxotes spp.",
    aliases: [],
    family: "archerfish",
    category: "both",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater", "brackish"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["mangrove", "canal", "river", "estuary"],
    regions: ["Thailand", "Southeast Asia"],
    summary: "A brackish/freshwater edge fish famous for surface/insect feeding around mangroves, canals, and overhanging cover.",
    feedingProfile: { primaryPrey: ["insects", "small fish", "shrimp"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Fly", "Small Shrimp", "Micro Jig", "Insect Imitation"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["surface feeder", "mangrove edge"],
    structureTags: ["overhanging cover", "mangroves", "canal edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["surface feeder", "mangrove edge"],
  }),
  species({
    regionPackIds: ["thailand"],
    id: "giant_freshwater_stingray",
    slug: "giant-freshwater-stingray",
    commonName: "Giant Freshwater Stingray",
    scientificName: "Urogymnus polylepis",
    aliases: [],
    family: "stingray",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["river", "estuary"],
    regions: ["Thailand", "Mekong Basin", "Southeast Asia"],
    summary: "A giant river ray profile for education-first FishIQ context around large rivers and conservation-sensitive species awareness.",
    feedingProfile: { primaryPrey: ["crustaceans", "mollusks", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Venue/guide appropriate bait"],
    bestTechniques: ["Use education-first context", "Check local rules and conservation status", "Avoid casual targeting claims"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["education_sensitive", "large river fish"],
    structureTags: ["deep river bends", "soft bottom", "large channels"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["education_sensitive", "large river fish"],
  }),
  species({
    regionPackIds: ["thailand"],
    id: "iridescent_shark",
    slug: "iridescent-shark",
    commonName: "Iridescent Shark / Sutchi Catfish",
    scientificName: "Pangasianodon hypophthalmus",
    aliases: ["Sutchi Catfish", "Pla Sawai"],
    family: "shark catfish",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "reservoir", "fishery"],
    regions: ["Thailand", "Southeast Asia"],
    summary: "A large pangasiid catfish common in managed fisheries and river systems; useful for venue/local FishIQ reads.",
    feedingProfile: { primaryPrey: ["pellets", "plant matter", "small fish", "detritus"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Dough Bait", "Bread", "Venue Bait", "Pellet Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "large catfish"],
    structureTags: ["fishery edges", "river channels", "deep water"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "large catfish"],
  }),
  species({
    regionPackIds: ["thailand", "indonesia"],
    id: "clown_featherback",
    slug: "clown-featherback",
    commonName: "Clown Featherback",
    scientificName: "Chitala ornata",
    aliases: ["Clown Knifefish"],
    family: "featherback",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "lake", "canal", "reservoir"],
    regions: ["Thailand", "Southeast Asia"],
    summary: "A predatory featherback often associated with slow rivers, canals, vegetation, and low-light ambush windows.",
    feedingProfile: { primaryPrey: ["small fish", "shrimp", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Minnow Plug", "Soft Plastic", "Live Bait", "Spinner"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "low-light feeder"],
    structureTags: ["vegetation", "canals", "slow pools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "low-light feeder"],
  }),
  species({
    regionPackIds: ["thailand", "indonesia"],
    id: "jungle_perch",
    slug: "jungle-perch",
    commonName: "Jungle Perch",
    scientificName: "Hampala macrolepidota / related spp.",
    aliases: ["Sebarau-style river predator"],
    family: "cyprinid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "creek", "reservoir"],
    regions: ["Thailand", "Indonesia", "Southeast Asia"],
    summary: "A fast river/reservoir predator often tied to clear current, bait, rock, and flowing edges.",
    feedingProfile: { primaryPrey: ["minnows", "small fish", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Spinner", "Small Minnow Plug", "Spoon", "Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["current-aware feeder", "roaming predator"],
    structureTags: ["riffles", "rock", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["current-aware feeder", "roaming predator"],
  }),
  species({
    regionPackIds: ["thailand"],
    id: "rohu_sea",
    slug: "rohu-sea",
    commonName: "Rohu",
    scientificName: "Labeo rohita",
    aliases: [],
    family: "cyprinid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "lake", "pond", "fishery"],
    regions: ["Thailand", "Southeast Asia", "Introduced fisheries"],
    summary: "A carp-like food/sport fish of ponds, fisheries, and river systems; useful for local logs and education.",
    feedingProfile: { primaryPrey: ["plant matter", "detritus", "plankton"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Dough Bait", "Bread", "Corn", "Plant-based Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "schooling"],
    structureTags: ["pond edges", "slow river edges", "fishery channels"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "schooling"],
  }),
  species({
    regionPackIds: ["thailand"],
    id: "mrigal_carp",
    slug: "mrigal-carp",
    commonName: "Mrigal Carp",
    scientificName: "Cirrhinus mrigala",
    aliases: [],
    family: "cyprinid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "lake", "pond", "fishery"],
    regions: ["Thailand", "Southeast Asia", "Introduced fisheries"],
    summary: "A bottom-feeding carp often found in managed fisheries and warmwater pond systems.",
    feedingProfile: { primaryPrey: ["detritus", "plant matter", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Dough Bait", "Corn", "Bread", "Pellet Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "local_common"],
    structureTags: ["pond bottom", "slow channels", "fishery edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "local_common"],
  }),
];

export const THAILAND_FISHIQ_SPECIES_BUFF_V2_SEED: FishIQSpeciesSeed[] = [
  ...SEA_SHARED_FISHIQ_SPECIES_BUFF_V2_SEED.filter((item) => item.regionPackIds?.includes('thailand' as FishIQSeaPackId)),
  ...THAILAND_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED,
];

const PHILIPPINES_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED: RoddoSeaFishIQSeed[] = [
  species({
    regionPackIds: ["philippines", "indonesia"],
    id: "bigeye_tuna",
    slug: "bigeye-tuna",
    commonName: "Bigeye Tuna",
    scientificName: "Thunnus obesus",
    aliases: [],
    family: "tuna",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["ocean", "drop-off", "pelagic"],
    regions: ["Philippines", "Indonesia", "Western Pacific"],
    summary: "A bluewater tuna profile for offshore FishIQ context around depth, bait, temperature breaks, and night/deep patterns.",
    feedingProfile: { primaryPrey: ["squid", "baitfish", "small pelagics"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Trolling Lure", "Live Bait", "Metal Jig", "Chunk Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["pelagic predator", "bait-tracker"],
    structureTags: ["drop-offs", "temperature breaks", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["pelagic predator", "bait-tracker"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia"],
    id: "black_marlin",
    slug: "black-marlin",
    commonName: "Black Marlin",
    scientificName: "Istiompax indica",
    aliases: [],
    family: "billfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["ocean", "reef edge", "drop-off"],
    regions: ["Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A trophy billfish associated with bluewater bait, reef drop-offs, current, and offshore structure.",
    feedingProfile: { primaryPrey: ["tuna", "mackerel", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Trolled Lure", "Live Bait", "Skip Bait"],
    bestTechniques: ["Use offshore/trophy context only", "Find bait and current first", "Handle/release according to local norms"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["trophy pelagic", "bait-tracker"],
    structureTags: ["drop-offs", "seamounts", "current lines"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["trophy pelagic", "bait-tracker"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia"],
    id: "blue_marlin_sea",
    slug: "blue-marlin-sea",
    commonName: "Blue Marlin",
    scientificName: "Makaira nigricans",
    aliases: [],
    family: "billfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["ocean", "pelagic", "drop-off"],
    regions: ["Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A trophy offshore predator tied to bait, current, temperature edges, and deep bluewater structure.",
    feedingProfile: { primaryPrey: ["tuna", "mahi-mahi", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Trolled Lure", "Skip Bait", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["trophy pelagic", "roaming predator"],
    structureTags: ["drop-offs", "current lines", "temperature breaks"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["trophy pelagic", "roaming predator"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "rainbow_runner",
    slug: "rainbow-runner",
    commonName: "Rainbow Runner",
    scientificName: "Elagatis bipinnulata",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["ocean", "reef edge", "pelagic"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A colorful schooling pelagic often associated with floating structure, reefs, and bait-rich current lines.",
    feedingProfile: { primaryPrey: ["small baitfish", "squid", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Jig", "Trolling Feather", "Live Bait", "Popping Plug"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "pelagic predator"],
    structureTags: ["FADs", "reef edges", "current lines"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "pelagic predator"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia"],
    id: "amberjack_sea",
    slug: "amberjack-sea",
    commonName: "Amberjack",
    scientificName: "Seriola spp.",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "wreck", "drop-off", "ocean"],
    regions: ["Philippines", "Indonesia", "Indo-Pacific"],
    summary: "A hard-pulling reef/drop-off jack often found around ledges, reefs, bait, and vertical structure.",
    feedingProfile: { primaryPrey: ["baitfish", "squid", "small jacks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Vertical Jig", "Live Bait", "Swimbait", "Popping Plug"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure predator", "bait-tracker"],
    structureTags: ["drop-offs", "reefs", "wrecks", "ledges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure predator", "bait-tracker"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "parrotfish_sea",
    slug: "parrotfish-sea",
    commonName: "Parrotfish",
    scientificName: "Scaridae spp.",
    aliases: [],
    family: "parrotfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["reef", "lagoon", "inshore"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A reef-grazing fish group included for education and local ID, not as aggressive targeting guidance.",
    feedingProfile: { primaryPrey: ["algae", "coral-associated growth", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Bait", "Algae Bait"],
    bestTechniques: ["Use education/ID context", "Respect reef rules and local norms", "Avoid overclaiming target value"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef grazer", "education"],
    structureTags: ["reef flats", "lagoons", "coral edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef grazer", "education"],
  }),
  species({
    regionPackIds: ["philippines", "indonesia", "thailand"],
    id: "surgeonfish_sea",
    slug: "surgeonfish-sea",
    commonName: "Surgeonfish",
    scientificName: "Acanthuridae spp.",
    aliases: [],
    family: "surgeonfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["reef", "lagoon", "inshore"],
    regions: ["Philippines", "Indonesia", "Thailand", "Indo-Pacific"],
    summary: "A common reef fish group useful for visual ID and ecology context around reefs and lagoons.",
    feedingProfile: { primaryPrey: ["algae", "detritus", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Algae Bait", "Tiny Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef grazer", "education"],
    structureTags: ["reef flats", "lagoons", "coral edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef grazer", "education"],
  }),
  species({
    regionPackIds: ["philippines"],
    id: "mudfish_dalag",
    slug: "mudfish-dalag",
    commonName: "Mudfish / Dalag",
    scientificName: "Channa striata",
    aliases: ["Dalag", "Striped Snakehead"],
    family: "snakehead",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["pond", "canal", "rice field", "marsh"],
    regions: ["Philippines"],
    summary: "Philippines-local alias profile for striped snakehead/mudfish around ponds, canals, rice-field edges, and weedy water.",
    feedingProfile: { primaryPrey: ["small fish", "frogs", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Topwater Frog", "Soft Plastic", "Spinnerbait", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "cover-oriented"],
    structureTags: ["weeds", "canal edges", "pond margins"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "cover-oriented"],
  }),
  species({
    regionPackIds: ["philippines"],
    id: "hito_catfish",
    slug: "hito-catfish",
    commonName: "Hito / Walking Catfish",
    scientificName: "Clarias batrachus / Clarias spp.",
    aliases: ["Hito"],
    family: "airbreathing catfish",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["pond", "canal", "ditch", "marsh"],
    regions: ["Philippines", "Southeast Asia"],
    summary: "A local freshwater catfish profile for warm ponds, canals, and low-oxygen edges.",
    feedingProfile: { primaryPrey: ["worms", "small fish", "insects", "detritus"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Chicken Liver", "Cut Bait", "Dough Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["scent/taste oriented", "bottom feeder"],
    structureTags: ["muddy edges", "canals", "pond bottoms"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["scent/taste oriented", "bottom feeder"],
  }),
  species({
    regionPackIds: ["philippines"],
    id: "apahap_barramundi",
    slug: "apahap-barramundi",
    commonName: "Apahap / Barramundi",
    scientificName: "Lates calcarifer",
    aliases: ["Apahap", "Asian Sea Bass"],
    family: "latid",
    category: "both",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater", "brackish", "saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["estuary", "river", "mangrove", "bay"],
    regions: ["Philippines"],
    summary: "Philippines-local alias profile for barramundi/Asian sea bass in estuary, river, and mangrove systems.",
    feedingProfile: { primaryPrey: ["baitfish", "shrimp", "prawns"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Paddle Tail", "Minnow Plug", "Shrimp Imitation", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "current-aware feeder"],
    structureTags: ["mangroves", "drains", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "current-aware feeder"],
  }),
];

export const PHILIPPINES_FISHIQ_SPECIES_BUFF_V2_SEED: FishIQSpeciesSeed[] = [
  ...SEA_SHARED_FISHIQ_SPECIES_BUFF_V2_SEED.filter((item) => item.regionPackIds?.includes('philippines' as FishIQSeaPackId)),
  ...PHILIPPINES_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED,
];

const INDONESIA_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED: RoddoSeaFishIQSeed[] = [
  species({
    regionPackIds: ["indonesia"],
    id: "asian_arowana",
    slug: "asian-arowana",
    commonName: "Asian Arowana",
    scientificName: "Scleropages formosus",
    aliases: [],
    family: "arowana",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["river", "blackwater", "swamp forest"],
    regions: ["Indonesia", "Southeast Asia"],
    summary: "A culturally important freshwater fish included as education-sensitive ID/ecology context, not casual targeting advice.",
    feedingProfile: { primaryPrey: ["insects", "small fish", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Education-only"],
    bestTechniques: ["Use education-first context", "Check local protection and trade rules", "Avoid target-style recommendations"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["surface predator", "education_sensitive"],
    structureTags: ["blackwater edges", "overhanging cover", "swamp forest"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["surface predator", "education_sensitive"],
  }),
  species({
    regionPackIds: ["indonesia"],
    id: "mahseer_sea",
    slug: "mahseer-sea",
    commonName: "Mahseer",
    scientificName: "Tor spp.",
    aliases: [],
    family: "cyprinid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["river", "rapids", "pool"],
    regions: ["Indonesia", "Southeast Asia"],
    summary: "A powerful river cyprinid group associated with clean current, boulders, deep pools, and forested rivers.",
    feedingProfile: { primaryPrey: ["fruit", "insects", "small fish", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Spinner", "Fruit/Local Bait", "Spoon", "Fly"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["current-aware feeder", "river fish"],
    structureTags: ["rapids", "boulder pools", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["current-aware feeder", "river fish"],
  }),
  species({
    regionPackIds: ["indonesia", "thailand"],
    id: "wallago_catfish",
    slug: "wallago-catfish",
    commonName: "Wallago Catfish",
    scientificName: "Wallago attu / Wallagonia spp.",
    aliases: [],
    family: "catfish",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["river", "lake", "reservoir"],
    regions: ["Indonesia", "Thailand", "Southeast Asia"],
    summary: "A large predatory catfish group associated with rivers, deeper pools, and bait-rich edges.",
    feedingProfile: { primaryPrey: ["fish", "frogs", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Live Bait", "Cut Bait", "Large Soft Plastic"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "large catfish"],
    structureTags: ["deep pools", "river bends", "current breaks"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "large catfish"],
  }),
  species({
    regionPackIds: ["indonesia"],
    id: "striped_catfish_patin",
    slug: "striped-catfish-patin",
    commonName: "Striped Catfish / Patin",
    scientificName: "Pangasianodon hypophthalmus / Pangasius spp.",
    aliases: ["Patin"],
    family: "shark catfish",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "pond", "fishery", "reservoir"],
    regions: ["Indonesia", "Southeast Asia"],
    summary: "A common catfish/food-fish profile for Indonesian ponds, fisheries, and river systems.",
    feedingProfile: { primaryPrey: ["pellets", "plant matter", "detritus", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Dough Bait", "Pellet Bait", "Bread", "Natural Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "large catfish"],
    structureTags: ["pond edges", "river channels", "fishery water"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "large catfish"],
  }),
  species({
    regionPackIds: ["indonesia"],
    id: "baronang_rabbitfish",
    slug: "baronang-rabbitfish",
    commonName: "Baronang / Rabbitfish",
    scientificName: "Siganus spp.",
    aliases: ["Baronang"],
    family: "rabbitfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "seagrass", "mangrove", "inshore"],
    regions: ["Indonesia"],
    summary: "Indonesia-local rabbitfish profile for seagrass, reef flats, mangrove edges, and local food-fish context.",
    feedingProfile: { primaryPrey: ["algae", "seagrass", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Algae Bait", "Bread", "Small Shrimp", "Tiny Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["grazing fish", "reef/seagrass"],
    structureTags: ["seagrass", "mangroves", "reef flats"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["grazing fish", "reef/seagrass"],
  }),
  species({
    regionPackIds: ["indonesia", "philippines"],
    id: "ruby_snapper",
    slug: "ruby-snapper",
    commonName: "Ruby Snapper",
    scientificName: "Etelis carbunculus / Etelis spp.",
    aliases: [],
    family: "snapper",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["deep reef", "drop-off", "ocean"],
    regions: ["Indonesia", "Philippines", "Indo-Pacific"],
    summary: "A deepwater snapper group tied to ledges, drop-offs, and depth-specific bottom structure.",
    feedingProfile: { primaryPrey: ["fish", "squid", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Slow Pitch Jig", "Cut Bait", "Squid", "Deep Drop Rig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["deep reef fish", "bottom predator"],
    structureTags: ["deep ledges", "drop-offs", "reef slopes"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["deep reef fish", "bottom predator"],
  }),
  species({
    regionPackIds: ["indonesia", "philippines"],
    id: "red_bass_snapper",
    slug: "red-bass-snapper",
    commonName: "Mangrove Red Snapper / Red Bass",
    scientificName: "Lutjanus bohar",
    aliases: ["Red Bass"],
    family: "snapper",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "lagoon", "ocean"],
    regions: ["Indonesia", "Philippines", "Indo-Pacific"],
    summary: "A strong reef snapper profile for tropical reef edges, passes, and bait-rich current.",
    feedingProfile: { primaryPrey: ["small fish", "crustaceans", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Stickbait", "Jig", "Live Bait", "Cut Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef predator", "ambush predator"],
    structureTags: ["reef passes", "coral edges", "drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef predator", "ambush predator"],
  }),
  species({
    regionPackIds: ["indonesia", "philippines"],
    id: "napoleon_wrasse",
    slug: "napoleon-wrasse",
    commonName: "Napoleon Wrasse",
    scientificName: "Cheilinus undulatus",
    aliases: ["Humphead Wrasse"],
    family: "wrasse",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["reef", "lagoon", "drop-off"],
    regions: ["Indonesia", "Philippines", "Indo-Pacific"],
    summary: "A large, conservation-sensitive reef fish included for education and ID awareness, not target guidance.",
    feedingProfile: { primaryPrey: ["mollusks", "crustaceans", "reef invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Education-only"],
    bestTechniques: ["Use education/ID context", "Check local protections", "Avoid target-style recommendations"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef fish", "education_sensitive"],
    structureTags: ["coral reefs", "drop-offs", "lagoons"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef fish", "education_sensitive"],
  }),
];

export const INDONESIA_FISHIQ_SPECIES_BUFF_V2_SEED: FishIQSpeciesSeed[] = [
  ...SEA_SHARED_FISHIQ_SPECIES_BUFF_V2_SEED.filter((item) => item.regionPackIds?.includes('indonesia' as FishIQSeaPackId)),
  ...INDONESIA_ONLY_FISHIQ_SPECIES_BUFF_V2_SEED,
];

export const SEA_FISHIQ_SPECIES_BUFF_V2_SEED: FishIQSpeciesSeed[] = [
  ...THAILAND_FISHIQ_SPECIES_BUFF_V2_SEED,
  ...PHILIPPINES_FISHIQ_SPECIES_BUFF_V2_SEED,
  ...INDONESIA_FISHIQ_SPECIES_BUFF_V2_SEED,
];
